<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable prettier/prettier -->
<template>
   <div ref="selectfieldEl" class="selectfield" :tabindex="tabindex">
      <div
         ref="selectionEl"
         class="selectfield--selection"
         :class="{ 'selectfield--selection-no-border': !border }"
         @click="onSelectfieldElClick"
      >
         <slot name="input" :model-value="modelValue">
            <input
               v-model="inputValue"
               type="text"
               :placeholder="placeholder"
               :readonly="!autocomplete"
               @input="onTyping"
            />
         </slot>
         <div v-if="multi && isModelValueNotEmpty()" ref="inputTagsEl" class="selectfield--selected-tags">
            <Tag :name="modelValue[0].name" @closeclick="select(modelValue[0])" />
            <Tag v-if="modelValue.length > 1" :name="'+' + (modelValue.length - 1)" :show-close-icon="false" />
         </div>
         <CloseIcon v-if="isModelValueNotEmpty() && clearIcon" @click="onClearClick" />
         <ChevronIcon @click="onExpandIconClick($event)" />
      </div>
      <Teleport to="body" :disabled="!isMobile">
         <SelectMenu :expanded="isExpanded" :mobile="isMobile" @close="collapse">
            <div class="selectfield--selection-mobile">
               <slot v-if="isMobile">
                  <div class="selectfield--selection-mobile-label">{{ label }}</div>
                  <input
                     v-if="autocomplete"
                     v-model="inputValue"
                     type="text"
                     :readonly="!autocomplete"
                     :placeholder="placeholder"
                     style="margin-top: var(--size-8)"
                     @input="onTyping"
                  />
               </slot>
            </div>
            <div ref="menuEl" class="selectfield--menu selectfield--menu-hidden" :class="menuCls">
               <slot name="option-top"></slot>
               <div v-if="multi" class="selectfield--selected-tags">
                  <div class="selectfield--selected-tags-title">{{ selectedText }}</div>
                  <Tag
                     v-for="(record, j) in modelValue"
                     :key="j"
                     :name="record.name"
                     @closeclick="select(record)"
                  />
                  <Tag
                     v-if="!isModelValueNotEmpty()"
                     :name="noSelectedText"
                     :show-close-icon="false"
                     class="selectfield--selected-tag-info"
                  />
               </div>
               <template v-for="(option, i) in partialData" :key="option.value">
                  <div v-if="option._grouper_" class="selectfield--item-group">
                     {{ option._grouper_ }}
                  </div>
                  <div
                     v-if="!option._grouper_"
                     class="selectfield--item"
                     :class="{'selectfield--item-selected': isSelected(option)}"
                     @click="select(option)"
                  >
                     <input v-if="multi" type="checkbox" :checked="isSelected(option)"/>
                     <slot name="option" :option="option" :inputValue="inputValue">item-{{ i }}</slot>
                  </div>
               </template>
               <slot v-if="partialData?.length === 0" name="option-no-items">
                  <div class="selectfield--no-items">{{ noResultText }}</div>
               </slot>
               <slot name="option-bottom"></slot>
            </div>
         </SelectMenu>
      </Teleport>
   </div>
</template>

<script lang="ts" setup>
import { ref, PropType, defineEmits, onMounted, watch } from "vue"
import ChevronIcon from "../../../assets/icons/expand_more.svg?component"
import CloseIcon from "../../../assets/icons/close.svg?component"
import { createPopper, Instance, Placement } from "@popperjs/core"
import { debounce } from "@/utils/debounce"
import SelectMenu from "@/components/Form/Select/atoms/SelectMenu.vue"
import Tag from "@/components/Form/Select/atoms/Tag.vue"
import { Collection } from "@/utils/collection"
import {
   isMobile as isMobileScreen,
   addReportWindowSizeCallback
} from "@/utils/screen"
import type { ISelectGrouper } from "./grouper"
import {
   onClickOutside,
   useResizeObserver,
   useInfiniteScroll
} from "@vueuse/core"

const props = defineProps({
   tabindex: {
      type: Number,
      default: 0
   },
   menuCls: {
      type: String,
      default: ""
   },
   placeholder: {
      type: String,
      default: ""
   },
   label: {
      type: String,
      default: "Wybierz ..."
   },
   selectedText: {
      type: String,
      default: "Wybrane"
   },
   noSelectedText: {
      type: String,
      default: "Brak wybranych elementów"
   },
   noResultText: {
      type: String,
      default: "Brak elementów"
   },
   clearIcon: {
      type: Boolean,
      default: true
   },
   border: {
      type: Boolean,
      default: true
   },
   autocomplete: {
      type: Boolean,
      default: false
   },
   options: {
      type: Array as PropType<any>,
      default() {
         return []
      }
   },
   initialValues: {
      type: Array as PropType<any>,
      default() {
         return []
      }
   },
   modelValue: {
      type: Object as PropType<any> | null,
      default: null
   },
   filter: {
      type: Function as PropType<(data: any[], text: string) => any[]> | null,
      default: null
   },
   menuPlacement: {
      type: String,
      default: "bottom-start"
   },
   menuWidth: {
      type: String,
      default: "none"
   },
   grouper: {
      type: Object as PropType<ISelectGrouper>,
      default: null
   },
   sortData: {
      type: Boolean,
      default: true
   },
   multi: {
      type: Boolean,
      default: false
   },
   visibleItemNumber: {
      type: Number,
      default: 50
   }
})

const emit = defineEmits<{
   (e: "input", value: string): void
   (e: "change", value: any): void
   (e: "update:modelValue", value: any): void
}>()

const getData = (data: any[]) => {
   if (props.grouper) {
      const grouped = Collection.groupByKey(data, props.grouper.property),
         result: any[] = []
      Object.keys(grouped).map((key) => {
         result.push({ _grouper_: key })
         result.push(
            ...(props.sortData
               ? Collection.sortByKey(grouped[key], "name", "ASC")
               : grouped[key])
         )
      })

      return result
   }

   return props.sortData ? Collection.sortByKey(data, "name", "ASC") : data
}

const getPartialData = (data: any[], start = 0) => {
   return data.slice(start, start + props.visibleItemNumber)
}

let popper: Instance
const selectfieldEl = ref(),
   selectionEl = ref(),
   menuEl = ref(),
   inputTagsEl = ref(),
   data = ref(getData(props.options)),
   partialData = ref([...getPartialData(data.value)]),
   isExpanded = ref(false),
   isMobile = ref(false),
   inputValue = ref("")

const expand = () => {
   if (isExpanded.value) {
      collapse()

      return
   }

   if (props.menuWidth === "same") {
      menuEl.value.style.width = selectionEl.value.clientWidth + "px"
   }

   popper ? popper.update() : null
   isExpanded.value = true
   selectionEl.value.classList.add("selectfield--expanded")
   menuEl.value.classList.remove("selectfield--menu-hidden")
}

const collapse = () => {
   setTimeout(() => {
      isExpanded.value = false
      selectionEl.value.classList.remove("selectfield--expanded")
      menuEl.value.classList.add("selectfield--menu-hidden")
      inputValue.value = props.modelValue?.["name"] || ""

      if (isMobile.value) {
         menuEl.value.scrollTop = 0
      }

      setTimeout(() => {
         try {
            data.value = getData(props.options)
            partialData.value = [...getPartialData(data.value)]
            menuEl.value.scrollTop = 0
         } catch (error) {
            //error
         }
      }, 300)
   }, 0)
}

const select = (record: any) => {
   let v = record

   if (props.multi) {
      v = [...props.modelValue]

      if (isSelected(record)) {
         v.splice(v.indexOf(record), 1)
      } else {
         v.push(record)
      }
   } else {
      collapse()
   }

   emit("change", v)
   emit("update:modelValue", v)

   popper ? popper.update() : null
}

const isSelected = (record: any) => {
   if (props.multi) {
      return props.modelValue.indexOf(record) > -1
   }

   return record === props.modelValue
}

const isModelValueNotEmpty = () => {
   if (props.multi) {
      return props.modelValue.length
   }

   return !!props.modelValue
}

const onClearClick = (ev: Event) => {
   ev.stopPropagation()
   emit("change", props.multi ? [] : null)
   emit("update:modelValue", props.multi ? [] : null)
   popper ? popper.update() : null
   inputValue.value = ""
}

const onTyping = debounce((ev: Event) => {
   const inputValue = (ev.target as HTMLInputElement).value

   if (props.filter) {
      data.value = getData(props.filter(props.options, inputValue))
      partialData.value = [...getPartialData(data.value)]
      menuEl.value.scrollTo({
         top: 0,
         left: 0,
         behavior: "smooth"
      })
   }

   emit("input", inputValue)
}, 300)

const onSelectfieldElClick = () => {
   if (!isExpanded.value) {
      expand()
   }
}

const onExpandIconClick = (ev: Event) => {
   ev.stopPropagation()

   isExpanded.value ? collapse() : expand()
}

onMounted(() => {
   const initPopper = () => {
         popper = createPopper(selectionEl.value, menuEl.value, {
            placement: props.menuPlacement as unknown as Placement,
            modifiers: [
               {
                  name: "offset",
                  options: {
                     offset: [0, 4]
                  }
               }
            ]
         })
      },
      initialRecord: any[] = []

   isMobile.value = isMobileScreen()

   if (props.initialValues.length) {
      props.initialValues.forEach((value: any) => {
         const record = props.options.find((record: any) => {
            return record.value === value
         })

         record ? initialRecord.push(record) : null
      })

      emit("update:modelValue", initialRecord)
   }

   if (!isMobile.value) {
      initPopper()
   }

   addReportWindowSizeCallback(() => {
      isMobile.value = isMobileScreen()
      if (isMobile.value && popper) {
         popper.destroy()
      }
   })

   onClickOutside(selectfieldEl.value, () => {
      if (isMobile.value || !isExpanded.value) {
         return
      }

      collapse()
   })

   useResizeObserver(inputTagsEl, (entries) => {
      const entry = entries[0]
      const { width } = entry.contentRect

      selectionEl.value.querySelector(
         "input"
      ).style.paddingLeft = `calc(var(--input-padding-side) + 8px + ${width}px)`
   })

   useInfiniteScroll(
      menuEl.value,
      () => {
         partialData.value.push(
            ...getPartialData(data.value, partialData.value.length)
         )
      },
      { distance: 10 }
   )
})

watch(
   () => props.modelValue,
   () => {
      if (props.multi && props.modelValue.length === 0) {
         selectionEl.value.querySelector(
            "input"
         ).style.paddingLeft = `var(--input-padding-side)`
      }

      if (!props.multi) {
         inputValue.value = props.modelValue?.["name"] || ""
      }
   }
)

watch(
   () => props.options,
   (newValue) => {
      data.value = getData(newValue)
      partialData.value = [...getPartialData(data.value)]
   }
)
</script>
